import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { isAuthenticated } from '@/lib/auth';
import { apiClient, api } from '@/lib/api-client';

interface Transaction {
  id: string;
  type: 'ONRAMP' | 'OFFRAMP';
  status: string;
  nextStep: string | null;
  walletAddress: string;
  countryCode: string;
  currency: string;
  cryptoCurrency: string;
  cryptoNetwork: string;
  amountLocal: string | null;
  localCurrency: string;
  user: {
    email: string;
  } | null;
  transaction: {
    id: string;
    amountUsd: string | null;
    amountLocal: string | null;
    localCurrency: string;
    cryptoAmount: string | null;
    cryptoCurrency: string;
    fxRate: string | null;
    status: string;
    settledAt: string | null;
    createdAt: string;
  } | null;
  paymentInstruction: {
    accountNumber: string;
    accountName: string;
    bankName: string;
    amountLocal: number;
    localCurrency: string;
    reference: string;
    expiresAt: string | null;
  } | null;
  cryptoInstruction: {
    walletAddress: string;
    cryptoAmount: number;
    cryptoCurrency: string;
    cryptoNetwork: string;
    reference: string;
    expiresAt: string | null;
  } | null;
  bankAccount: {
    accountNumber: string;
    accountName: string;
    bankName: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
}

export default function TransactionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [refundSuccess, setRefundSuccess] = useState(false);
  const [showRefundConfirm, setShowRefundConfirm] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    loadTransaction();
  }, [id, navigate]);

  const loadTransaction = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/transactions/${id}`);
      setTransaction(response.data.session);
    } catch (err: any) {
      console.error('Failed to load transaction:', err);
      setError(err.response?.data?.error || 'Failed to load transaction details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      COMPLETED: 'bg-green-100 text-green-700',
      FAILED: 'bg-red-100 text-red-700',
      KYC_FAILED: 'bg-red-100 text-red-700',
      PENDING: 'bg-yellow-100 text-yellow-700',
      PROCESSING: 'bg-blue-100 text-blue-700',
      EXPIRED: 'bg-gray-100 text-gray-700',
      PENDING_IDENTITY: 'bg-yellow-100 text-yellow-700',
      PENDING_OTP: 'bg-yellow-100 text-yellow-700',
      PENDING_KYC: 'bg-yellow-100 text-yellow-700',
      PENDING_BANK_ACCOUNT: 'bg-yellow-100 text-yellow-700',
      PENDING_PAYMENT: 'bg-blue-100 text-blue-700',
      PAYMENT_RECEIVED: 'bg-blue-100 text-blue-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatCurrency = (amount: string | number, currency: string = 'NGN') => {
    const numAmount = typeof amount === 'string' ? Number(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(numAmount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const isRefundEligible = () => {
    if (!transaction) return false;

    // Only ONRAMP can be refunded
    if (transaction.type !== 'ONRAMP') return false;

    // Check if already refunded
    if (transaction.transaction?.status === 'REFUNDED') return false;

    // Check status eligibility
    const eligibleStatuses = ['COMPLETED', 'PAYMENT_RECEIVED', 'FAILED'];
    return eligibleStatuses.includes(transaction.status);
  };

  const handleRefund = async () => {
    if (!id) return;

    setRefundLoading(true);
    setRefundError(null);
    setRefundSuccess(false);

    try {
      await api.transactions.refund(id);
      setRefundSuccess(true);
      setShowRefundConfirm(false);

      // Reload transaction to show updated status
      setTimeout(() => {
        loadTransaction();
      }, 1000);
    } catch (err: any) {
      console.error('Refund failed:', err);
      setRefundError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to process refund request'
      );
    } finally {
      setRefundLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-gray-600">Loading transaction details...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !transaction) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <Button onClick={() => navigate('/dashboard/transactions')} variant="outline">
              ← Back to Transactions
            </Button>
          </div>
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-red-600 mb-4">{error || 'Transaction not found'}</p>
              <Button onClick={() => navigate('/dashboard/transactions')}>
                Back to Transactions
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button onClick={() => navigate('/dashboard/transactions')} variant="outline" size="sm">
              ← Back to Transactions
            </Button>
            <h1 className="text-3xl font-bold text-gray-900 mt-4">Transaction Details</h1>
            <p className="text-gray-600 mt-1">Session ID: {transaction.id}</p>
          </div>
          <div>
            <span className={`px-4 py-2 text-sm font-medium rounded ${getStatusColor(transaction.status)}`}>
              {transaction.status}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Session Information */}
          <Card>
            <CardHeader>
              <CardTitle>Session Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Type</span>
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  transaction.type === 'ONRAMP' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                }`}>
                  {transaction.type}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status</span>
                <span className="font-medium">{transaction.status}</span>
              </div>
              {transaction.nextStep && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Next Step</span>
                  <span className="font-medium">{transaction.nextStep}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">User Email</span>
                <span className="font-medium">{transaction.user?.email || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Created</span>
                <span className="font-medium text-sm">{formatDate(transaction.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Updated</span>
                <span className="font-medium text-sm">{formatDate(transaction.updatedAt)}</span>
              </div>
              {transaction.expiresAt && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Expires</span>
                  <span className="font-medium text-sm">{formatDate(transaction.expiresAt)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Crypto Details */}
          <Card>
            <CardHeader>
              <CardTitle>Crypto Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Currency</span>
                <span className="font-medium">{transaction.cryptoCurrency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Network</span>
                <span className="font-medium">{transaction.cryptoNetwork}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-gray-600">Wallet Address</span>
                <div className="text-right">
                  <span className="font-mono text-xs block">{transaction.walletAddress}</span>
                  <button
                    onClick={() => copyToClipboard(transaction.walletAddress)}
                    className="text-blue-600 hover:text-blue-800 text-xs mt-1"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Country</span>
                <span className="font-medium">{transaction.countryCode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Fiat Currency</span>
                <span className="font-medium">{transaction.currency}</span>
              </div>
            </CardContent>
          </Card>

          {/* Transaction Details */}
          {transaction.transaction && (
            <Card>
              <CardHeader>
                <CardTitle>Transaction Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Transaction ID</span>
                  <span className="font-mono text-xs">{transaction.transaction.id.substring(0, 8)}...</span>
                </div>
                {transaction.transaction.amountUsd && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount (USD)</span>
                    <span className="font-medium">{formatCurrency(transaction.transaction.amountUsd, 'USD')}</span>
                  </div>
                )}
                {transaction.transaction.amountLocal && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount ({transaction.transaction.localCurrency})</span>
                    <span className="font-medium">{formatCurrency(transaction.transaction.amountLocal, transaction.transaction.localCurrency)}</span>
                  </div>
                )}
                {transaction.transaction.cryptoAmount && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Crypto Amount</span>
                    <span className="font-medium">
                      {transaction.transaction.cryptoAmount} {transaction.transaction.cryptoCurrency}
                    </span>
                  </div>
                )}
                {transaction.transaction.fxRate && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">FX Rate</span>
                    <span className="font-medium">{transaction.transaction.fxRate}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Transaction Status</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(transaction.transaction.status)}`}>
                    {transaction.transaction.status}
                  </span>
                </div>
                {transaction.transaction.settledAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Settled At</span>
                    <span className="font-medium text-sm">{formatDate(transaction.transaction.settledAt)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Payment Instructions (Onramp) */}
          {transaction.paymentInstruction && (
            <Card>
              <CardHeader>
                <CardTitle>Payment Instructions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-gray-600">Account Number</span>
                  <div className="text-right">
                    <span className="font-medium">{transaction.paymentInstruction.accountNumber}</span>
                    <button
                      onClick={() => copyToClipboard(transaction.paymentInstruction!.accountNumber)}
                      className="text-blue-600 hover:text-blue-800 text-xs ml-2"
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Account Name</span>
                  <span className="font-medium">{transaction.paymentInstruction.accountName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Bank</span>
                  <span className="font-medium">{transaction.paymentInstruction.bankName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount</span>
                  <span className="font-medium text-lg">
                    {formatCurrency(transaction.paymentInstruction.amountLocal, transaction.paymentInstruction.localCurrency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Reference</span>
                  <span className="font-mono text-xs">{transaction.paymentInstruction.reference}</span>
                </div>
                {transaction.paymentInstruction.expiresAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expires</span>
                    <span className="font-medium text-sm">{formatDate(transaction.paymentInstruction.expiresAt)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Crypto Instructions (Offramp) */}
          {transaction.cryptoInstruction && (
            <Card>
              <CardHeader>
                <CardTitle>Crypto Instructions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-gray-600">Deposit Address</span>
                  <div className="text-right">
                    <span className="font-mono text-xs block">{transaction.cryptoInstruction.walletAddress}</span>
                    <button
                      onClick={() => copyToClipboard(transaction.cryptoInstruction!.walletAddress)}
                      className="text-blue-600 hover:text-blue-800 text-xs mt-1"
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount</span>
                  <span className="font-medium text-lg">
                    {transaction.cryptoInstruction.cryptoAmount} {transaction.cryptoInstruction.cryptoCurrency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Network</span>
                  <span className="font-medium">{transaction.cryptoInstruction.cryptoNetwork}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Reference</span>
                  <span className="font-mono text-xs">{transaction.cryptoInstruction.reference}</span>
                </div>
                {transaction.cryptoInstruction.expiresAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expires</span>
                    <span className="font-medium text-sm">{formatDate(transaction.cryptoInstruction.expiresAt)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Bank Account (Offramp) */}
          {transaction.bankAccount && (
            <Card>
              <CardHeader>
                <CardTitle>Payout Bank Account</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Account Number</span>
                  <span className="font-medium">{transaction.bankAccount.accountNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Account Name</span>
                  <span className="font-medium">{transaction.bankAccount.accountName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Bank</span>
                  <span className="font-medium">{transaction.bankAccount.bankName}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center gap-4">
          <div>
            {refundSuccess && (
              <div className="px-4 py-2 bg-green-50 border border-green-200 rounded-md text-green-800 text-sm">
                ✅ Refund request submitted successfully
              </div>
            )}
            {refundError && (
              <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
                ❌ {refundError}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            {isRefundEligible() && !showRefundConfirm && (
              <Button
                onClick={() => setShowRefundConfirm(true)}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                Request Refund
              </Button>
            )}
            <Button onClick={loadTransaction} variant="outline">
              Refresh
            </Button>
          </div>
        </div>

        {/* Refund Confirmation Modal */}
        {showRefundConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Confirm Refund</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to refund this transaction?
              </p>

              <div className="bg-gray-50 p-4 rounded-md mb-6 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Transaction ID:</span>
                  <span className="font-mono text-xs">{transaction?.id.slice(0, 8)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium">{transaction?.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="font-medium">{transaction?.status}</span>
                </div>
                {transaction?.transaction && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium">
                      {transaction.transaction.cryptoAmount} {transaction.transaction.cryptoCurrency}
                    </span>
                  </div>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> The customer will receive their funds back to their original payment method. This action cannot be undone.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowRefundConfirm(false);
                    setRefundError(null);
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={refundLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRefund}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  disabled={refundLoading}
                >
                  {refundLoading ? 'Processing...' : 'Confirm Refund'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
